"use client"

import {
  CenterAligned,
  FlexColumn,
  FormControl,
  List,
  ListItem,
  ListItemAvatar,
  ListItemContent,
  ListItemDescription,
  ListItemSide,
  ListItemTitle,
  Spinner,
  TextBody,
  TextDisplay,
} from "@opensea/ui-kit"
import { useMountEffect } from "@opensea/ui-kit/hooks"
import { PhoneAndroid, VerifiedUser } from "@opensea/ui-kit/icons"
import { useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { AuthCode } from "../../../components/AuthCode"
import { ModalBackButton } from "../../../components/Modal"
import { PasteFromClipboardButton } from "../../../components/PasteFromClipboardButton"
import {
  MfaChallengeActions,
  MfaChallengeState,
} from "../../../stores/mfaChallengeStore"
import {
  MfaHandlerActions,
  MfaHandlerState,
} from "../../../stores/mfaHandlerStore"

const SelectChallengeOptionStep = () => {
  const t = useTranslations("wallet.MfaChallenge")
  const { initializingStep } = useSnapshot(MfaChallengeState)

  return (
    <FlexColumn className="gap-8 pt-12 pb-6">
      <FlexColumn className="gap-4">
        <TextDisplay size="sm">{t("chooseSecurityOption")}</TextDisplay>
        <TextBody color="text-secondary" size="sm">
          {t("mfaRequired")}
        </TextBody>
      </FlexColumn>
      <List>
        <ListItem
          onClick={async () =>
            await MfaChallengeActions.selectChallengeOption("sms")
          }
        >
          <ListItemAvatar icon={PhoneAndroid} />
          <ListItemContent>
            <ListItemTitle>{t("sms")}</ListItemTitle>
            <ListItemDescription className="text-start">
              {t("smsDescription")}
              <br />
              {t("smsRegionRestriction")}
            </ListItemDescription>
          </ListItemContent>
          <ListItemSide>
            {initializingStep === "challenge-sms" && <Spinner />}
          </ListItemSide>
        </ListItem>
        <ListItem
          onClick={async () =>
            await MfaChallengeActions.selectChallengeOption("totp")
          }
        >
          <ListItemAvatar icon={VerifiedUser} />
          <ListItemContent>
            <ListItemTitle>{t("authenticator")}</ListItemTitle>
            <ListItemDescription className="text-start">
              {t("authenticatorDescription")}
              <br />
              {t("authenticatorSecurityNote")}
            </ListItemDescription>
          </ListItemContent>
          <ListItemSide>
            {initializingStep === "challenge-totp" && <Spinner />}
          </ListItemSide>
        </ListItem>
      </List>
    </FlexColumn>
  )
}

export const ChallengeStep = () => {
  const t = useTranslations("wallet.MfaChallenge")
  const [code, setCode] = useState("")
  const { step, submissionError } = useSnapshot(MfaChallengeState)
  const { mfaMethods } = useSnapshot(MfaHandlerState)

  const onSubmit = async (currentCode: string) => {
    setCode(currentCode)
    const success = await MfaChallengeActions.submitChallenge(currentCode)
    if (success) {
      MfaHandlerActions.completeChallenge()
    }
  }

  const authCodeDeliveryDetails =
    step.id === "challenge-sms"
      ? t("smsCodeInstructions")
      : t("authenticatorCodeInstructions")

  return (
    <>
      <FlexColumn className="gap-8 pt-12 pb-6">
        <FlexColumn className="gap-4">
          <TextDisplay className="leading-[28px]" size="sm">
            {t("mfaRequiredTitle")}
          </TextDisplay>
          <TextBody color="text-secondary" size="sm">
            {authCodeDeliveryDetails}
          </TextBody>
        </FlexColumn>
        <FormControl
          error={submissionError}
          hideLabel
          label={t("authenticationCodeLabel")}
          overrides={{
            Caption: {
              className: "justify-center",
            },
          }}
        >
          <AuthCode
            autoFocus
            error={Boolean(submissionError)}
            onChange={setCode}
            onComplete={onSubmit}
            value={code}
          />
        </FormControl>
        <PasteFromClipboardButton
          className="mt-8 md:hidden"
          onPaste={onSubmit}
        />
      </FlexColumn>
      {mfaMethods.length > 1 && (
        <ModalBackButton onBack={MfaChallengeActions.onBack} />
      )}
    </>
  )
}

export const MfaChallenge = () => {
  const { step, submitting, initializingStep } = useSnapshot(MfaChallengeState)
  const { mfaMethods } = useSnapshot(MfaHandlerState)

  useMountEffect(() => {
    if (mfaMethods.length === 1 && initializingStep === undefined) {
      MfaChallengeActions.selectChallengeOption(mfaMethods[0])
    }
  })

  if (submitting || initializingStep) {
    return (
      <CenterAligned className="h-[400px]">
        <Spinner />
      </CenterAligned>
    )
  }

  switch (step.id) {
    case "select-challenge-option":
      return <SelectChallengeOptionStep />
    case "challenge-sms":
    case "challenge-totp":
      return <ChallengeStep />
  }
}
